<script setup lang="ts">
import { ref, onMounted } from 'vue'

/**
 * Consent banner for analytics + ads.
 *
 * Third-party scripts (GTM analytics, AdSense) never load before the user
 * grants consent. The choice is stored in localStorage('cutmp3-consent'):
 *  - 'granted' -> banner hides, GTM + AdSense load (deferred to idle time)
 *  - 'denied'  -> banner hides, nothing third-party loads
 *  - null      -> banner shows on every page until a choice is made
 */

const CONSENT_KEY = 'cutmp3-consent'

const show = ref(false)

const strings: Record<string, { text: string; accept: string; decline: string }> = {
  he: {
    text: 'אנו משתמשים בקובצי Cookie לניתוח תעבורה ולפרסומות. כלי הניתוח והפרסומות נטענים רק לאחר אישורכם.',
    accept: 'אישור',
    decline: 'דחייה'
  },
  en: {
    text: 'We use cookies for traffic analytics and ads. Ads and analytics load only after you approve.',
    accept: 'Accept',
    decline: 'Decline'
  },
  es: {
    text: 'Usamos cookies para analítica de tráfico y publicidad. Los anuncios y la analítica se cargan solo tras tu aprobación.',
    accept: 'Aceptar',
    decline: 'Rechazar'
  },
  pt: {
    text: 'Usamos cookies para análise de tráfego e anúncios. Anúncios e análise são carregados só após a sua aprovação.',
    accept: 'Aceitar',
    decline: 'Recusar'
  },
  fr: {
    text: 'Nous utilisons des cookies pour l\u2019analytique et la publicité. Les publicités et l\u2019analytique se chargent uniquement après votre accord.',
    accept: 'Accepter',
    decline: 'Refuser'
  },
  de: {
    text: 'Wir verwenden Cookies für Analyse und Werbung. Werbung und Analyse werden erst nach Ihrer Zustimmung geladen.',
    accept: 'Akzeptieren',
    decline: 'Ablehnen'
  },
  it: {
    text: 'Usiamo cookie per analisi del traffico e pubblicità. Pubblicità e analisi si caricano solo dopo la tua approvazione.',
    accept: 'Accetta',
    decline: 'Rifiuta'
  },
  ru: {
    text: 'Мы используем cookie для аналитики и рекламы. Реклама и аналитика загружаются только после вашего согласия.',
    accept: 'Принять',
    decline: 'Отклонить'
  },
  ja: {
    text: '当サイトでは分析と広告にCookieを使用します。広告・分析ツールは同意後に読み込まれます。',
    accept: '同意する',
    decline: '拒否する'
  }
}

const s = ref(strings.en)

function decide(choice: 'granted' | 'denied') {
  try {
    localStorage.setItem(CONSENT_KEY, choice)
  } catch (e) { /* storage unavailable - remember for this pageview only */ }
  show.value = false
  if (choice === 'granted') {
    const w = window as any
    if (typeof w.__loadThirdParty === 'function') w.__loadThirdParty()
  }
}

onMounted(() => {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(CONSENT_KEY)
  } catch (e) { /* noop */ }

  const lang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase()
  s.value = strings[lang] ?? strings.en

  if (stored === null) show.value = true
})
</script>

<template>
  <Transition name="consent">
    <div v-if="show" class="consent-banner" role="region" aria-label="Cookie consent">
      <p class="consent-text">
        {{ s.text }}
      </p>
      <div class="consent-actions">
        <button class="consent-btn consent-accept" @click="decide('granted')">
          {{ s.accept }}
        </button>
        <button class="consent-btn consent-decline" @click="decide('denied')">
          {{ s.decline }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: calc(var(--z-modal) + 100);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  padding: 0.875rem 1rem;
  background: var(--text-primary);
  color: #f9fafb;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.18);
}

.consent-text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  max-width: 640px;
}

.consent-actions {
  display: flex;
  gap: 0.625rem;
}

.consent-btn {
  min-height: 44px;
  min-width: 44px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: filter var(--transition-fast), background-color var(--transition-fast);
}

.consent-accept {
  background: var(--primary-color);
  color: #1a1a1a;
}

.consent-accept:hover {
  filter: brightness(1.08);
}

.consent-decline {
  background: transparent;
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.consent-decline:hover {
  background: rgba(255, 255, 255, 0.12);
}

.consent-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

/* Enter/leave: transform only (no layout impact) */
.consent-enter-active,
.consent-leave-active {
  transition: transform var(--transition-normal), opacity var(--transition-normal);
}

.consent-enter-from,
.consent-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
